import React from "react";
import { block, asset, C, Icon, Eyebrow, Screenshot, Media, CompareSlider, MultilineText } from "./shared";

/* ── pipeline band ────────────────────────────────────────────── */
export function Pipeline() {
  const b = block("pipeline");
  if (!b) return null;
  return (
    <section id="pipeline" className="fr-sweep" style={{ position: "relative", margin: "64px clamp(14px,3vw,28px) 0", borderRadius: "var(--radius-xl)", overflow: "hidden", boxShadow: "var(--shadow-md)" }} data-screen-label="Pipeline">
      <img src={asset("locations/river-arches.jpg")} alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
      <div style={{ position: "absolute", inset: 0, background: "linear-gradient(100deg, rgba(44,43,38,0.84) 0%, rgba(44,43,38,0.56) 55%, rgba(44,43,38,0.3) 100%)" }}></div>
      <div className="reveal" style={{ position: "relative", padding: "clamp(40px,5vw,60px) clamp(24px,5vw,56px)" }}>
        <Eyebrow light>{b.eyebrow}</Eyebrow>
        <h2 style={{ fontSize: "clamp(26px,3.2vw,40px)", color: "#FFFDF8", margin: "12px 0 14px", letterSpacing: "-0.02em", lineHeight: 1.08, maxWidth: 680, textWrap: "balance" }}>
          <MultilineText text={b.title} />
        </h2>
        <p style={{ fontSize: 16.5, lineHeight: 1.6, color: "var(--clay-2)", margin: "0 0 30px", maxWidth: 580 }}>
          {b.body}
        </p>
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
          {(b.stages || []).map((s, i) => (
            <React.Fragment key={s}>
              <span className="reveal fr-lift" style={{ "--i": i, fontFamily: "var(--font-mono)", fontSize: 13, fontWeight: 500, color: "var(--clay-1)", padding: "7px 13px", borderRadius: "var(--radius-pill)", background: "rgba(250,246,238,0.12)", border: "1px solid rgba(250,246,238,0.2)", whiteSpace: "nowrap", backdropFilter: "blur(4px)", cursor: "default" }}>{s}</span>
              {i < b.stages.length - 1 && <span className="fr-chev" style={{ "--i": i, display: "inline-flex" }}><Icon name="chevron-right" size={15} color="rgba(243,221,210,0.7)" /></span>}
            </React.Fragment>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── promo reel: continues the hero on the cream page ────────────
   The video (per-locale file from the admin) loads only when the band
   approaches the viewport, loops muted while at least a third of it is on
   screen, and "Watch with sound" restarts it from the top with audio.
   Controls use inline SVG: lucide icons are drawn once per page render
   and would not follow the sound toggle. */
const REEL_ICONS = {
  play: <polygon points="6 3 20 12 6 21 6 3" />,
  soundOff: <React.Fragment><path d="M11 4.702a.705.705 0 0 0-1.203-.498L6.413 7.587A1.4 1.4 0 0 1 5.416 8H3a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1h2.416a1.4 1.4 0 0 1 .997.413l3.383 3.384A.705.705 0 0 0 11 19.298z" /><line x1="22" x2="16" y1="9" y2="15" /><line x1="16" x2="22" y1="9" y2="15" /></React.Fragment>,
  soundOn: <React.Fragment><path d="M11 4.702a.705.705 0 0 0-1.203-.498L6.413 7.587A1.4 1.4 0 0 1 5.416 8H3a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1h2.416a1.4 1.4 0 0 1 .997.413l3.383 3.384A.705.705 0 0 0 11 19.298z" /><path d="M16 9a5 5 0 0 1 0 6" /><path d="M19.364 18.364a9 9 0 0 0 0-12.728" /></React.Fragment>,
  fullscreen: <React.Fragment><path d="M8 3H5a2 2 0 0 0-2 2v3" /><path d="M21 8V5a2 2 0 0 0-2-2h-3" /><path d="M3 16v3a2 2 0 0 0 2 2h3" /><path d="M16 21h3a2 2 0 0 0 2-2v-3" /></React.Fragment>,
};
function ReelIcon({ name, size = 18 }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill={name === "play" ? "currentColor" : "none"} stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{REEL_ICONS[name]}</svg>;
}
// Size lives in .fr-reel-pill (landing.css) so phones get a compact variant.
// Poster = a frame of the reel itself (the "From script to finished episode" title card),
// so it matches the page language without a separate image upload.
const POSTER_AT = 3.4;
const reelPill = { display: "inline-flex", alignItems: "center", borderRadius: "var(--radius-pill)", border: "1px solid rgba(250,246,238,0.22)", background: "rgba(20,19,17,0.55)", backdropFilter: "blur(10px)", WebkitBackdropFilter: "blur(10px)", color: "#FFFDF8", fontFamily: "var(--font-body)", fontWeight: 600, cursor: "pointer" };

function ReelPlayer({ media, label, onDuration }) {
  const frameRef = React.useRef(null);
  const videoRef = React.useRef(null);
  const onScreen = React.useRef(false);
  const fresh = React.useRef(true); // still showing the poster frame
  const [load, setLoad] = React.useState(false);
  const [muted, setMuted] = React.useState(true);
  const [started, setStarted] = React.useState(false);
  const [progress, setProgress] = React.useState(0);
  const reduced = React.useMemo(() => typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches, []);

  React.useEffect(() => {
    const el = frameRef.current;
    if (!el || !("IntersectionObserver" in window)) { setLoad(true); return; }
    const near = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setLoad(true); near.disconnect(); } }, { rootMargin: "800px 0px" });
    const seen = new IntersectionObserver(([e]) => {
      onScreen.current = e.intersectionRatio >= 0.35;
      const v = videoRef.current;
      if (!v) return;
      if (onScreen.current && (!reduced || started)) {
        if (fresh.current) { fresh.current = false; v.currentTime = 0; }
        v.play().catch(() => {});
      } else v.pause();
    }, { threshold: [0, 0.35, 0.7] });
    near.observe(el); seen.observe(el);
    return () => { near.disconnect(); seen.disconnect(); };
  }, [reduced, started]);

  const withSound = () => {
    const v = videoRef.current;
    if (!v) return;
    if (muted) { fresh.current = false; v.currentTime = 0; v.muted = false; setMuted(false); setStarted(true); v.play().catch(() => {}); }
    else { v.muted = true; setMuted(true); }
  };
  const fullscreen = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.requestFullscreen) v.requestFullscreen().catch(() => {});
    else if (v.webkitEnterFullscreen) v.webkitEnterFullscreen();
  };

  return (
    <div ref={frameRef} className="fr-reel" style={{ position: "relative", aspectRatio: "16 / 9", borderRadius: "var(--radius-xl)", overflow: "hidden", background: "var(--clay-9)" }}>
      <video ref={videoRef} src={load ? media.url : undefined} muted loop playsInline preload="metadata" aria-label={label}
        onLoadedMetadata={(e) => { onDuration && onDuration(e.currentTarget.duration); if (fresh.current) e.currentTarget.currentTime = POSTER_AT; }}
        onTimeUpdate={(e) => setProgress(e.currentTarget.duration ? e.currentTarget.currentTime / e.currentTarget.duration : 0)}
        onClick={withSound}
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", display: "block", cursor: "pointer" }} />
      {reduced && !started && (
        <button type="button" onClick={withSound} aria-label={C.reel.play}
          style={{ ...reelPill, position: "absolute", left: "50%", top: "50%", transform: "translate(-50%,-50%)", width: 78, height: 78, padding: 0, justifyContent: "center", borderRadius: "50%" }}>
          <ReelIcon name="play" size={26} />
        </button>
      )}
      {/* The reel carries its own REC timecode bottom-right and logo top-left,
          so the controls take the two free corners and progress is the bar. */}
      <div className="fr-reel-bar" style={{ position: "absolute", left: 0, bottom: 0, padding: "clamp(12px,2vw,20px)", pointerEvents: "none" }}>
        <button type="button" className="fr-reel-pill" onClick={withSound} style={{ ...reelPill, pointerEvents: "auto" }} aria-pressed={!muted}>
          <ReelIcon name={muted ? "soundOff" : "soundOn"} />
          <span className="fr-reel-label">{muted ? C.reel.soundOn : C.reel.soundOff}</span>
          {muted && <span className="fr-reel-label-short">{C.reel.soundOnShort}</span>}
        </button>
      </div>
      <div className="fr-reel-bar" style={{ position: "absolute", right: 0, top: 0, padding: "clamp(12px,2vw,20px)", pointerEvents: "none" }}>
        <button type="button" className="fr-reel-pill fr-reel-square" onClick={fullscreen} aria-label={C.reel.fullscreen} title={C.reel.fullscreen} style={{ ...reelPill, justifyContent: "center", pointerEvents: "auto" }}>
          <ReelIcon name="fullscreen" size={17} />
        </button>
      </div>
      <div aria-hidden="true" style={{ position: "absolute", left: 0, bottom: 0, height: 3, width: `${progress * 100}%`, background: "var(--accent)", boxShadow: "0 0 12px rgb(var(--glow-rgb) / 0.8)" }} />
    </div>
  );
}

export function Showreel() {
  const b = block("showreel");
  const [duration, setDuration] = React.useState(0);
  if (!b) return null;
  const hasVideo = b.media && (b.media.content_type || "").startsWith("video/");
  const mono = { fontFamily: "var(--font-mono)", fontSize: 12.5, letterSpacing: "0.08em", color: "var(--text-muted)" };
  return (
    <section id="promo" style={{ maxWidth: 1220, margin: "0 auto", padding: "clamp(28px,4vw,44px) clamp(20px,5vw,56px) 0", scrollMarginTop: 76 }} data-screen-label="Promo">
      <div className="reveal" style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14, ...mono, textTransform: "uppercase" }}>
        <span style={{ width: 7, height: 7, borderRadius: "50%", background: "var(--accent)" }}></span>
        <span style={{ color: "var(--accent-text)", fontWeight: 600 }}>{b.eyebrow}</span>
        {duration > 0 && <span>· {Math.floor(duration / 60)}:{String(Math.round(duration % 60)).padStart(2, "0")}</span>}
      </div>
      <div className="reveal" style={{ "--i": 1 }}>
        {hasVideo ? <ReelPlayer media={b.media} label={b.title} onDuration={setDuration} /> : (
          <div style={{ position: "relative", aspectRatio: "16 / 9", borderRadius: "var(--radius-xl)", overflow: "hidden", background: "linear-gradient(160deg, var(--clay-8), var(--clay-9))", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--clay-1)" }}>
            {b.media && <Media media={b.media} alt={b.title} style={{ position: "absolute", inset: 0, opacity: 0.55 }} />}
            <span style={{ position: "relative", width: 74, height: 74, borderRadius: "50%", background: "rgba(250,246,238,0.1)", border: "1px solid rgba(250,246,238,0.22)", display: "flex", alignItems: "center", justifyContent: "center" }}><ReelIcon name="play" size={26} /></span>
          </div>
        )}
      </div>
      {b.note && <div className="reveal" style={{ "--i": 2, marginTop: 14, ...mono }}>{b.note}</div>}
    </section>
  );
}

/* ── compact grid of everything else the platform does ─────────── */
export function Capabilities() {
  const b = block("capabilities");
  if (!b) return null;
  return (
    <section id="capabilities" style={{ maxWidth: 1220, margin: "0 auto", padding: "88px clamp(20px,5vw,56px) 0", scrollMarginTop: 76 }} data-screen-label="Capabilities">
      <div className="reveal" style={{ marginBottom: 30 }}>
        <Eyebrow>{b.eyebrow}</Eyebrow>
        <h2 style={{ fontSize: "clamp(27px,3.2vw,40px)", margin: "12px 0 0", letterSpacing: "-0.02em", lineHeight: 1.08, textWrap: "balance" }}>
          <MultilineText text={b.title} />
        </h2>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", gap: 14 }}>
        {(b.items || []).map((item, idx) => (
          <div key={idx} className="reveal fr-lift" style={{ "--i": idx % 3, display: "grid", gap: 10, alignContent: "start", padding: "20px 22px", borderRadius: "var(--radius-md)", background: "var(--surface-card)", border: "1px solid var(--border-soft)", boxShadow: "var(--shadow-clay-inset), var(--shadow-xs)", cursor: "default" }}>
            <span style={{ display: "inline-flex", width: 38, height: 38, alignItems: "center", justifyContent: "center", borderRadius: "var(--radius-sm)", background: "var(--tile-bg)" }}>
              <Icon name={item.icon || "sparkles"} size={19} color="var(--tile-fg)" />
            </span>
            <h4 style={{ margin: 0, fontSize: 15.5, fontWeight: 700, fontFamily: "var(--font-display)", color: "var(--text-display)" }}>{item.title}</h4>
            <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.5, color: "var(--text-muted)" }}>{item.text}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ── product tour intro ───────────────────────────────────────── */
export function FeaturesIntro() {
  const b = block("features_intro");
  if (!b) return null;
  return (
    <div id="features" className="reveal" style={{ textAlign: "center", padding: "88px clamp(20px,5vw,56px) 8px", scrollMarginTop: 76 }}>
      <Eyebrow>{b.eyebrow}</Eyebrow>
      <h2 style={{ fontSize: "clamp(30px,3.6vw,46px)", margin: "12px auto 0", letterSpacing: "-0.025em", maxWidth: 680, lineHeight: 1.06, textWrap: "balance" }}>
        <MultilineText text={b.title} />
      </h2>
    </div>
  );
}

/* ── one showcase: copy beside a screenshot in a dark app window ─ */
export function Showcase({ id, blockKey, flip, dark }) {
  const b = block(blockKey);
  if (!b) return null;

  const text = (
    <div className="reveal" style={{ minWidth: 0, maxWidth: 520 }}>
      <Eyebrow light={dark}>{b.eyebrow}</Eyebrow>
      <h3 style={{ fontSize: "clamp(26px,2.9vw,37px)", margin: "12px 0 22px", letterSpacing: "-0.022em", lineHeight: 1.1, textWrap: "balance", color: dark ? "var(--ink-text-display)" : "var(--text-display)" }}>
        <MultilineText text={b.title} />
      </h3>
      <div style={{ display: "grid", gap: 18 }}>
        {(b.bullets || []).map((bullet, idx) => (
          <div key={idx} className="reveal" style={{ "--i": idx, display: "flex", gap: 14, alignItems: "flex-start" }}>
            <span style={{ display: "inline-flex", width: 38, height: 38, alignItems: "center", justifyContent: "center", borderRadius: "var(--radius-sm)", flex: "none", background: dark ? "var(--tile-ink-bg)" : "var(--tile-bg)" }}>
              <Icon name={bullet.icon || "sparkles"} size={19} color={dark ? "var(--tile-ink-fg)" : "var(--tile-fg)"} />
            </span>
            <div style={{ minWidth: 0 }}>
              <h4 style={{ margin: 0, fontSize: 16, fontWeight: 700, fontFamily: "var(--font-display)", color: dark ? "var(--ink-text-display)" : "var(--text-display)" }}>{bullet.title}</h4>
              <p style={{ margin: "3px 0 0", fontSize: 14, lineHeight: 1.55, color: dark ? "var(--ink-text-muted)" : "var(--text-muted)" }}>{bullet.text}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const cropped = Number(b.media?.zoom || 1) > 1.01 && b.media?.aspect;
  const bleedClass = dark && cropped && !b.compare ? (flip ? " fr-media-bleed-left" : " fr-media-bleed-right") : "";
  const media = (
    <div className={"reveal fr-showcase-media" + bleedClass} style={{ "--i": 1, minWidth: 0 }}>
      {b.compare
        ? <CompareSlider before={b.media} after={b.media_after} aspect={b.media?.aspect || "16:10"} labels={C.compareLabels} />
        : <Screenshot media={b.media} alt={b.title} />}
    </div>
  );

  const inner = (
    <div className="fr-showcase-grid" style={{ display: "grid", gridTemplateColumns: flip ? "1.15fr 0.85fr" : "0.85fr 1.15fr", gap: "clamp(28px,4.5vw,72px)", alignItems: "center" }}>
      {flip ? media : text}
      {flip ? text : media}
    </div>
  );

  if (dark) {
    return (
      <section id={id} style={{ margin: "72px clamp(14px,3vw,28px)", borderRadius: "var(--radius-xl)", background: "var(--surface-ink)", border: "1px solid var(--ink-border)", overflow: "hidden", position: "relative", scrollMarginTop: 76 }} data-screen-label={blockKey}>
        <div aria-hidden="true" style={{ position: "absolute", inset: 0, zIndex: 0, pointerEvents: "none", backgroundImage: "radial-gradient(var(--ink-dot) 1px, transparent 1.4px)", backgroundSize: "26px 26px", maskImage: "radial-gradient(120% 90% at 30% 30%, #000 12%, transparent 70%)", WebkitMaskImage: "radial-gradient(120% 90% at 30% 30%, #000 12%, transparent 70%)" }}></div>
        <div style={{ position: "relative", zIndex: 1, padding: "clamp(40px,5vw,72px)" }}>
          {inner}
        </div>
      </section>
    );
  }

  return (
    <section id={id} style={{ maxWidth: 1220, margin: "0 auto", padding: "72px clamp(20px,5vw,56px) 0", scrollMarginTop: 76 }} data-screen-label={blockKey}>
      {inner}
    </section>
  );
}

/* ── AI infrastructure stats band ─────────────────────────────── */
export function Infrastructure() {
  const b = block("infrastructure");
  if (!b) return null;
  return (
    <section id="infra" style={{ margin: "88px clamp(14px,3vw,28px) 0", borderRadius: "var(--radius-xl)", background: "var(--bg-sunken)", border: "1px solid var(--border-soft)", overflow: "hidden", scrollMarginTop: 76 }} data-screen-label="Infrastructure">
      <div style={{ padding: "clamp(40px,5vw,64px) clamp(24px,5vw,56px)" }}>
        <div className="reveal" style={{ maxWidth: 640 }}>
          <Eyebrow>{b.eyebrow}</Eyebrow>
          <h2 style={{ fontSize: "clamp(27px,3.2vw,40px)", margin: "12px 0 14px", letterSpacing: "-0.02em", lineHeight: 1.08, textWrap: "balance" }}>
            <MultilineText text={b.title} />
          </h2>
          <p style={{ fontSize: 16.5, lineHeight: 1.6, color: "var(--text-muted)", margin: 0 }}>{b.body}</p>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 14, margin: "32px 0 26px" }}>
          {(b.stats || []).map((s, idx) => (
            <div key={idx} className="reveal fr-lift" style={{ "--i": idx, padding: "20px 22px", borderRadius: "var(--radius-md)", background: "var(--surface-card)", border: "1px solid var(--border-soft)", boxShadow: "var(--shadow-clay-inset), var(--shadow-xs)", cursor: "default" }}>
              <div style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 32, letterSpacing: "-0.02em", color: "var(--accent-text)", lineHeight: 1 }}>{s.value}</div>
              <div style={{ marginTop: 7, fontSize: 13.5, color: "var(--text-muted)", lineHeight: 1.4 }}>{s.label}</div>
            </div>
          ))}
        </div>
        <div className="reveal" style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
          {(b.models || []).map((m) => (
            <span key={m} style={{ fontFamily: "var(--font-mono)", fontSize: 12.5, fontWeight: 500, color: "var(--text-body)", padding: "6px 12px", borderRadius: "var(--radius-pill)", background: "var(--surface-card)", border: "1px solid var(--border-soft)" }}>{m}</span>
          ))}
          <span style={{ fontFamily: "var(--font-mono)", fontSize: 12.5, color: "var(--text-faint)" }}>{C.infraMore}</span>
        </div>
      </div>
    </section>
  );
}
